import { Dialog, DialogContent, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useEffect, useState } from "react";
import { DialogTitleClosable } from "../../components/DialogTitleClosable";
import { BatteryEventDto } from "./dtos/BatteryEventDto";
import { formatDate, formatDateTime, formatVoltage, sortComparatorDateTimeString } from "../../utils";
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import Typography from "@mui/material/Typography";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { batteryEvents } from "../../data/batteryEvents.data";



type BatteryEventsProps = {

    batteryEventDtos: BatteryEventDto[];
    displayBatteryEvents: boolean;
    doorName: string;
    onClose(): void;

};


type DataRow = {

    event: string;
    eventDateTime: Date;
    voltage: string;
    voltageLevel: number;

};


const dialogSx = {

    '.MuiPaper-root': { 
        maxWidth: 'fit-content'
    }

};


enum DisplayMode {

    Chart,
    Table

};


const compareRows = ( a: DataRow, b: DataRow ) => {

    if ( a.eventDateTime < b.eventDateTime ){
      
        return -1;

    }
    
    if ( a.eventDateTime > b.eventDateTime ){
      
        return 1;

    }

    return 0;

}


const columnDefinitions: GridColDef[ ] = [

    {
        field: 'event',
        headerName: 'Event',
        width: 200,
        editable: true,
        headerAlign: 'center',
        align: 'center'
    },

    {
        field: 'eventDateTime',
        headerName: 'Event Date Time',
        width: 200,
        editable: true,
        headerAlign: 'center',
        align: 'center',
        valueFormatter: v => formatDateTime( v.value ),
        sortComparator: sortComparatorDateTimeString,
    },

    {
        field: 'voltage',
        headerName: 'Voltage Level',
        width: 150,
        editable: true,
        headerAlign: 'center',
        align: 'center'
    }

];


export const BatteryEvents: React.FC<BatteryEventsProps> = ( props: BatteryEventsProps ) => {

    const { batteryEventDtos, displayBatteryEvents, doorName, onClose } = props;

    const [ columns, setColumns ] = useState< GridColDef[ ] >( columnDefinitions );
    const [ displayMode, setDisplayMode ] = useState( DisplayMode.Table );
    const [ open, setOpen ] = useState<boolean>( false );
    const [ pageSize, setPageSize ] = useState<number>( 10 );
    const [ rows, setRows ] = useState<DataRow[]>( [] );
   
    
    useEffect( () => {

        if( !displayBatteryEvents ) {

            return;

        }

        setOpen( true );

        const rows: DataRow[] = [];

        for( const batteryEventDto of batteryEventDtos) {

            const batteryEvent = batteryEvents.find( be => be.event === batteryEventDto.event );

            if( !batteryEvent ) {
                
                throw Error( `Battery event ${batteryEventDto.event} not found` );

            }
            
            rows.push({

                event: batteryEventDto.event,
                eventDateTime: batteryEventDto.eventDateTime,
                voltage: batteryEvent.displayedText,
                voltageLevel: batteryEventDto.voltageLevel ?? 5000

            });

        }

        setRows( rows );
        rows.sort( compareRows );

    }, [displayBatteryEvents] );


    const displayModeChangeHandler = ( _: React.MouseEvent<HTMLElement>, newDisplayMode: DisplayMode ) => {
     
        if( newDisplayMode !== null ) {

            setDisplayMode( newDisplayMode );

        }

    }


    const handleClose = ( ) => {
        
        
        setOpen( false );
        onClose( );

    }
  

    return (

        <Dialog
            onClose={ handleClose }
            open={ open }
            sx={ dialogSx }
        >
            <DialogTitleClosable onClose={ handleClose }>
                Events for { doorName }
            </DialogTitleClosable>
            
            <DialogContent dividers>

                
                <Stack direction="column" spacing={ 1 }>

                    <ToggleButtonGroup
                        color="primary"
                        value={displayMode}
                        exclusive
                        onChange={ displayModeChangeHandler }
                    >

                        <ToggleButton value={ DisplayMode.Table } sx={{ textTransform: 'none' }}>Table View</ToggleButton>
                        <ToggleButton value={ DisplayMode.Chart } sx={{ textTransform: 'none' }}>Chart View</ToggleButton>

                    </ToggleButtonGroup>
                
                    {
                        displayMode === DisplayMode.Table
                            &&
                        <DataGrid
                            getRowId={ _ => Math.floor( Math.random( ) * 100_000_000 ) }
                            rows={rows}
                            columns={columns}
                            disableSelectionOnClick
                            sx={{ width: 600 }}
                            pageSize={pageSize}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            rowsPerPageOptions={[5, 10, 20]}
                            pagination
                            autoHeight={true}
                        />


                    }

                    {
                        displayMode === DisplayMode.Chart
                            &&
                        <Stack direction="column">
                            
                            <Typography sx={{fontWeight:'bold', marginLeft:'auto', marginRight:'auto' }}>
                                Voltage Level
                            </Typography>
                        
                            <AreaChart data={rows} margin={{ top: 15, right: 5, left: 50, bottom: 100 }} width={700} height={400}>
                                <defs>
                                    <linearGradient id="color" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>

                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis tickFormatter={ tick => formatDate( tick ) } dataKey="eventDateTime" angle={-90} dy={50}/>
                                <YAxis type="number" domain={ [3000, 'dataMax'] } tickFormatter={ tick => formatVoltage( tick )}/>
                                <YAxis type="number" domain={ [3000, 'dataMax'] } />
                                <Tooltip labelFormatter={ value => `Date: ${formatDateTime( value )}` } formatter={ value => [formatVoltage( +value ), 'Voltage' ] }/>
                                <Area dataKey="voltageLevel" stroke="#8884d8" fillOpacity={1} fill="url(#color)" />
                            </AreaChart>

                        </Stack> 
                    }

                    

                </Stack>

            </DialogContent>
        </Dialog>

    );

};